import React, { useState, useEffect, useCallback } from "react";
import { Shield, RefreshCw, TrendingUp, AlertTriangle, Users, Clock } from "lucide-react";
import { api } from "../lib/apiClient";
import { Button } from "../components/ui/button";

const PATTERN_LABELS = {
  ignore_previous: "Ignorar instrucciones previas",
  role_swap_you_are_now: "Cambio de rol",
  system_role_inject: "Inyección de rol system",
  reveal_system_prompt: "Revelar prompt de sistema",
  output_format_hijack: "Hijack de formato",
  jailbreak_dan: "Jailbreak DAN",
  override_safety: "Bypass de filtros",
  instruction_marker: "Marcador de instrucción",
  data_exfiltration: "Exfiltración de datos",
  prompt_terminator: "Terminador de prompt",
};

const KIND_LABELS = {
  prompt_injection_attempt: "Prompt Injection",
  honeypot_triggered: "Honeypot",
  bad_ua: "User-Agent sospechoso",
  captcha_fail: "Captcha fallido",
  rate_limited: "Rate limit",
  too_fast_form: "Formulario instantáneo",
  disposable_email: "Email desechable",
};

const GRADE_STYLES = {
  A: { bg: "bg-emerald-50", border: "border-emerald-300", text: "text-emerald-800", ring: "ring-emerald-500" },
  B: { bg: "bg-green-50", border: "border-green-300", text: "text-green-800", ring: "ring-green-500" },
  C: { bg: "bg-amber-50", border: "border-amber-300", text: "text-amber-800", ring: "ring-amber-500" },
  D: { bg: "bg-orange-50", border: "border-orange-300", text: "text-orange-800", ring: "ring-orange-500" },
  F: { bg: "bg-rose-50", border: "border-rose-300", text: "text-rose-800", ring: "ring-rose-500" },
};

function formatSeconds(s) {
  if (s == null) return "—";
  if (s < 60) return `${Math.round(s)}s`;
  if (s < 3600) return `${Math.round(s / 60)}min`;
  if (s < 86400) return `${(s / 3600).toFixed(1)}h`;
  return `${(s / 86400).toFixed(1)}d`;
}

export default function AdminSecurityScorePage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [windowDays, setWindowDays] = useState(30);
  const [reloadTick, setReloadTick] = useState(0);

  useEffect(() => {
    let alive = true;
    Promise.resolve().then(() => { if (alive) setLoading(true); });
    api.get(`/admin/security/posture-score?window_days=${windowDays}`)
      .then(({ data: res }) => { if (alive) setData(res); })
      .catch(() => {})
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [windowDays, reloadTick]);

  const refresh = useCallback(() => setReloadTick((t) => t + 1), []);

  const gradeStyle = data ? GRADE_STYLES[data.grade] || GRADE_STYLES.C : GRADE_STYLES.A;

  return (
    <div className="space-y-6" data-testid="admin-security-score-page">
      <header className="flex flex-wrap items-center justify-between border-b border-gray-200 pb-3 gap-3">
        <div>
          <h1 className="font-serif text-2xl text-gray-900">Security Posture Score</h1>
          <p className="text-xs text-gray-500 mt-1">
            Indicador compuesto de la postura de seguridad de la plataforma. Útil para justificar
            inversión en ciberseguridad ante auditorías internas.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={windowDays}
            onChange={(e) => setWindowDays(Number(e.target.value))}
            className="h-9 px-2 text-xs border border-gray-300 rounded-sm bg-white"
            data-testid="window-days-select"
          >
            <option value={7}>Últimos 7 días</option>
            <option value={30}>Últimos 30 días</option>
            <option value={90}>Últimos 90 días</option>
            <option value={180}>Últimos 180 días</option>
          </select>
          <Button
            variant="outline"
            onClick={refresh}
            disabled={loading}
            className="rounded-sm h-9 text-xs"
            data-testid="score-refresh"
          >
            <RefreshCw size={14} className={`mr-1.5 ${loading ? "animate-spin" : ""}`} /> Actualizar
          </Button>
        </div>
      </header>

      {loading && !data && (
        <div className="text-center py-12 text-sm text-gray-500" data-testid="score-loading">
          Calculando score…
        </div>
      )}

      {data && (
        <>
          {/* Score principal */}
          <div className={`border-2 ${gradeStyle.border} ${gradeStyle.bg} p-6 sm:p-8`} data-testid="score-card">
            <div className="flex flex-wrap items-center gap-6">
              <div className={`w-32 h-32 rounded-full bg-white border-4 ${gradeStyle.border} flex flex-col items-center justify-center shadow-sm`} data-testid="score-gauge">
                <div className={`font-serif text-5xl ${gradeStyle.text}`} data-testid="score-value">{data.score}</div>
                <div className="text-[10px] uppercase tracking-widest text-gray-500">de 100</div>
              </div>
              <div className="flex-1 min-w-[220px]">
                <div className="flex items-center gap-2 mb-1">
                  <span className={`text-3xl font-serif ${gradeStyle.text}`} data-testid="score-grade">{data.grade}</span>
                  <span className={`text-base ${gradeStyle.text}`}>{data.grade_label}</span>
                </div>
                <p className="text-xs text-gray-700 leading-relaxed max-w-2xl">
                  Score calculado sobre {data.total_events} evento(s) de seguridad en los últimos {data.window_days} días.
                  La defensa multicapa anti prompt-injection neutraliza el 100% de los intentos detectados
                  (sanitization + wrap UNTRUSTED + system-prompt hardening + Pydantic validation + whitelist normativa).
                </p>
              </div>
            </div>
          </div>

          {/* KPIs */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <KpiCard
              icon={<AlertTriangle size={16} className="text-amber-600" />}
              label="Intentos de inyección"
              value={data.prompt_injection_count}
              suffix={data.prompt_injection_count > 0 ? "100% neutralizados" : "Limpio"}
              testid="kpi-prompt-injection"
            />
            <KpiCard
              icon={<TrendingUp size={16} className="text-rose-600" />}
              label="Patrones distintos"
              value={(data.top_patterns || []).length}
              suffix={(data.top_patterns || []).length > 5 ? "Diversidad alta" : "Manejable"}
              testid="kpi-top-patterns"
            />
            <KpiCard
              icon={<Users size={16} className="text-indigo-600" />}
              label="Usuarios reincidentes"
              value={(data.recurring_users || []).length}
              suffix={(data.recurring_users || []).length > 0 ? "Requieren atención" : "Sin reincidencias"}
              testid="kpi-recurring"
            />
            <KpiCard
              icon={<Clock size={16} className="text-emerald-600" />}
              label="MTBA"
              value={formatSeconds(data.mean_seconds_between_attempts)}
              suffix="Mean Time Between Attempts"
              testid="kpi-mtba"
            />
          </div>

          {/* Top patrones de ataque */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div className="border border-gray-200 bg-white" data-testid="top-patterns-card">
              <div className="px-4 py-3 border-b border-gray-200">
                <h2 className="font-serif text-base">Tipos de ataque más comunes</h2>
              </div>
              <div className="p-4">
                {(data.top_patterns || []).length === 0 ? (
                  <p className="text-xs text-gray-500" data-testid="top-patterns-empty">Sin intentos en la ventana seleccionada.</p>
                ) : (
                  <ul className="space-y-2">
                    {data.top_patterns.map((p) => {
                      const pct = data.prompt_injection_count > 0
                        ? Math.round((p.count / data.prompt_injection_count) * 100)
                        : 0;
                      return (
                        <li key={p.pattern} data-testid={`pattern-row-${p.pattern}`}>
                          <div className="flex items-center justify-between text-xs mb-1">
                            <span className="text-gray-700">{PATTERN_LABELS[p.pattern] || p.pattern}</span>
                            <span className="font-mono text-gray-600">{p.count} ({pct}%)</span>
                          </div>
                          <div className="h-1.5 bg-gray-100 rounded-sm overflow-hidden">
                            <div className="h-full bg-amber-500" style={{ width: `${pct}%` }} />
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            </div>

            {/* Usuarios reincidentes */}
            <div className="border border-gray-200 bg-white" data-testid="recurring-users-card">
              <div className="px-4 py-3 border-b border-gray-200">
                <h2 className="font-serif text-base">Usuarios reincidentes</h2>
                <p className="text-[10px] text-gray-500 mt-0.5">Usuarios con 2+ intentos en la ventana</p>
              </div>
              <div className="p-4">
                {(data.recurring_users || []).length === 0 ? (
                  <p className="text-xs text-gray-500" data-testid="recurring-users-empty">
                    Sin reincidencias. La gran mayoría de intentos son únicos o por bots automatizados.
                  </p>
                ) : (
                  <ul className="space-y-1.5">
                    {data.recurring_users.map((u, i) => (
                      <li key={i} className="flex items-center justify-between text-xs border-b border-gray-100 last:border-0 py-1" data-testid={`recurring-user-${i}`}>
                        <code className="text-gray-700 font-mono">{u.user_id}</code>
                        <div className="text-right">
                          <div className="text-rose-700 font-medium">{u.attempts} intentos</div>
                          {u.last_attempt && (
                            <div className="text-[10px] text-gray-500">
                              último: {new Date(u.last_attempt).toLocaleString("es-CO")}
                            </div>
                          )}
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          </div>

          {/* By kind (todos los eventos de seguridad) */}
          <div className="border border-gray-200 bg-white" data-testid="by-kind-card">
            <div className="px-4 py-3 border-b border-gray-200">
              <h2 className="font-serif text-base">Eventos por tipo</h2>
              <p className="text-[10px] text-gray-500 mt-0.5">Todos los `security_events` registrados en la ventana</p>
            </div>
            <div className="p-4">
              {(data.by_kind || []).length === 0 ? (
                <p className="text-xs text-gray-500">Sin eventos.</p>
              ) : (
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
                  {data.by_kind.map((k) => (
                    <div key={k.kind} className="border border-gray-100 px-3 py-2 rounded-sm" data-testid={`kind-${k.kind}`}>
                      <div className="text-[10px] uppercase tracking-widest text-gray-500">{KIND_LABELS[k.kind] || k.kind}</div>
                      <div className="font-serif text-xl text-gray-900">{k.count}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Cómo se calcula */}
          <details className="border border-gray-200 bg-gray-50 rounded-sm" data-testid="how-score-works">
            <summary className="px-4 py-3 cursor-pointer text-xs font-medium text-gray-700">
              ¿Cómo se calcula este score?
            </summary>
            <div className="px-4 pb-4 text-xs text-gray-700 leading-relaxed space-y-2">
              <p>El score parte de 100 y descuenta puntos según señales que indican entorno hostil:</p>
              <ul className="list-disc ml-5 space-y-1">
                <li>Más de 50 intentos de inyección en ventana → -15 pts</li>
                <li>Entre 11 y 50 intentos → -8 pts</li>
                <li>Cualquier intento positivo (≥1) → -3 pts</li>
                <li>Cada usuario reincidente (2+ intentos) → -3 pts (máx -15)</li>
                <li>Más de 5 tipos de patrón distintos → -5 pts (sofisticación del atacante)</li>
                <li>Más de 200 eventos totales → -5 pts (densidad de tráfico hostil)</li>
              </ul>
              <p className="pt-2">
                <strong>Importante:</strong> todos los intentos detectados son neutralizados automáticamente
                por la defensa multicapa: sanitización + wrap UNTRUSTED + system prompt hardening +
                validación Pydantic + filtros de whitelist normativa. Un score bajo NO significa que la
                aplicación haya sido comprometida, sino que está bajo presión activa de actores hostiles.
              </p>
              <p className="pt-1 text-[11px] text-gray-500">
                Escala: A (90-100) Excelente · B (75-89) Bueno · C (60-74) Aceptable · D (40-59) Atención · F (&lt;40) Crítico.
              </p>
            </div>
          </details>
        </>
      )}
    </div>
  );
}

function KpiCard({ icon, label, value, suffix, testid }) {
  return (
    <div className="border border-gray-200 bg-white p-3" data-testid={testid}>
      <div className="flex items-center justify-between text-[10px] uppercase tracking-widest text-gray-500 mb-1">
        <span>{label}</span>
        {icon}
      </div>
      <div className="font-serif text-2xl text-gray-900">{value}</div>
      {suffix && <div className="text-[10px] text-gray-500 mt-0.5">{suffix}</div>}
    </div>
  );
}
